import { FireIcon, TrophyIcon } from '@heroicons/react/24/outline';
import React, { useMemo, useState } from 'react';
import { useAccount } from 'wagmi';
import Button from '../components/Button';
import EarningsChart from '../components/dashboard/EarningsChart';
import RewardsTable from '../components/dashboard/RewardsTable';
import StatsCards from '../components/dashboard/StatsCards';
import WalletConnect from '../components/WalletConnect';
// import { AdBanner } from '../components/ads'; // Disabled for now
import { useApp } from '../hooks/useApp';
import { useTheme } from '../contexts/ThemeContext';
import { useUserRewards, useUserStats } from '../hooks/queries';
import useLocalStates from '../stores/localStates';
import clsxm from '../utils/clsxm';

const DashboardPage = () => {
  const { username } = useApp();
  const { _isDarkMode } = useTheme();
  const [selectedTimeframe, setSelectedTimeframe] = useState('7d');
  const { isConnected, address } = useAccount();
  // Get user data
  const { data: userStats, isLoading: statsLoading, error: statsError } = useUserStats(address);
  const { data: userRewards, isLoading: rewardsLoading, error: rewardsError } = useUserRewards(address);
  const { openClaimModal: isClaimModalOpen } = useLocalStates();

  // Use real data or fallback to defaults when no data is available
  const statsData = userStats || {
    totalGames: 0,
    gamesWon: 0,
    gamesLost: 0,
    winRate: 0,
    totalEarnings: 0,
    currentStreak: 0,
    bestStreak: 0,
    averageScore: 0,
    totalRewards: 0,
    rank: 'Rookie',
  };

  const rewardsData = useMemo(() => userRewards?.rewards || [], [userRewards]);

  const earningsData = useMemo(() => {
    if (!rewardsData.length) {
      // If no real data, generate minimal mock data
      const days = 7;
      const data = [];

      for (let i = days; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);

        data.push({
          date: date.toISOString().split('T')[0],
          earnings: 0,
          games: 0,
        });
      }

      return data;
    }

    // Generate earnings data from real rewards
    const daysToShow =
      selectedTimeframe === '24h' ? 1 : selectedTimeframe === '7d' ? 7 : selectedTimeframe === '30d' ? 30 : 90;

    const dateMap = new Map();

    // Initialize date map
    for (let i = daysToShow; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateKey = date.toISOString().split('T')[0];
      dateMap.set(dateKey, { earnings: 0, games: 0 });
    }

    // Process real rewards data
    rewardsData.forEach((game) => {
      if (game.endTime) {
        const gameDate = new Date(game.endTime);
        const dateKey = gameDate.toISOString().split('T')[0];

        if (dateMap.has(dateKey)) {
          const dayData = dateMap.get(dateKey);
          dayData.games += 1;

          // Add earnings if the user won and claimed rewards
          if (game.isWinner && game.claimed && game.claimTransaction?.status === 'completed') {
            dayData.earnings += game.claimTransaction.claimAmount; // Based on backend logic
          }
        }
      }
    });

    // Convert map to array
    return Array.from(dateMap.entries()).map(([date, data]) => ({
      date,
      earnings: data.earnings,
      games: data.games,
    }));
  }, [rewardsData, selectedTimeframe]);

  // Show loading state
  if (statsLoading || rewardsLoading) {
    return (
      <div className='min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto'></div>
          <p className='mt-4 text-gray-600 dark:text-gray-400'>Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  // Show error state if wallet is not connected
  if (!isConnected || !address) {
    return (
      <div className='min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center'>
        <div className='text-center p-8'>
          <h2 className='text-lg font-bold text-gray-900 dark:text-white mb-4'>Connect Your Wallet</h2>
          <p className='text-gray-600 dark:text-gray-400 mb-6'>
            Please connect your wallet to view your dashboard and rewards data.
          </p>
          <WalletConnect />
        </div>
      </div>
    );
  }

  // Show error state for API errors
  if (statsError || rewardsError) {
    return (
      <div className='min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center'>
        <div className='text-center p-8'>
          <h2 className='text-lg font-bold text-gray-900 dark:text-white mb-4'>Error Loading Dashboard</h2>
          <p className='text-gray-600 dark:text-gray-400 mb-6'>
            {statsError?.message || rewardsError?.message || 'Failed to load dashboard data'}
          </p>
          <Button onClick={() => window.location.reload()}>Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className={clsxm('space-y-4 md:space-y-8 w-full', isClaimModalOpen && 'overflow-hidden h-[calc(100vh-65px)]')}>
      {/* Header */}
      <div className='bg-white/90 dark:bg-gray-800/90 rounded-2xl p-4 md:p-6 shadow-lg border border-gray-200/50 dark:border-gray-700/50'>
        <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
          <div>
            <h1 className='text-lg md:text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 bg-clip-text text-transparent'>
              Dashboard
            </h1>
            <p className='mt-2 text-sm md:text-lg text-gray-600 dark:text-gray-300'>
              Welcome back,{' '}
              <span className='font-semibold text-blue-600 dark:text-blue-400'>{username || 'Player'}</span>
            </p>
          </div>

          {/* <Button onClick={connectWallet}>
            Connect Wallet
            </Button> */}
          <div className='w-full sm:w-auto'>
            {/* Time filter */}
            <select
              value={selectedTimeframe}
              onChange={(e) => setSelectedTimeframe(e.target.value)}
              className='w-full sm:w-auto px-4 py-3 border-0 bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm rounded-xl text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none shadow-lg transition-all duration-200'>
              <option value='24h'>Last 24 hours</option>
              <option value='7d'>Last 7 days</option>
              <option value='30d'>Last 30 days</option>
              <option value='all'>All time</option>
            </select>
          </div>
        </div>
      </div>
      <WalletConnect />

      {/* Banner Ad - Disabled for now */}
      {/* <AdBanner provider="auto" format="horizontal" className="hidden md:block" /> */}

      {/* Stats Cards */}
      <StatsCards stats={statsData} />

      {/* Charts and Tables Grid */}
      <div className='grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-8'>
        {/* Earnings Chart */}
        <div className='lg:col-span-2 order-2 lg:order-1'>
          <div className='bg-white/95 dark:bg-gray-800/95 rounded-2xl shadow-xl p-4 md:p-8 border border-gray-200/50 dark:border-gray-700/50'>
            <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 md:mb-8 gap-2'>
              <h3 className='text-sm md:text-base font-bold text-gray-900 dark:text-white'>Earnings Over Time</h3>
              <div className='flex items-center space-x-2 text-xs md:text-sm font-medium text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30 px-2 md:px-3 py-1 md:py-2 rounded-full'>
                <FireIcon className='h-3 w-3 md:h-4 md:w-4' />
                <span>Trending up</span>
              </div>
            </div>
            <EarningsChart data={earningsData} timeframe={selectedTimeframe} />
          </div>
        </div>

        {/* Quick Stats */}
        <div className='space-y-4 md:space-y-6 order-1 lg:order-2'>
          {/* Rank Card */}
          <div className='bg-white/95 dark:bg-gray-800/95 rounded-2xl shadow-xl p-4 md:p-6 border border-gray-200/50 dark:border-gray-700/50 hover:shadow-2xl transition-shadow duration-300 relative'>
            <div className='flex items-center justify-between'>
              <div className='flex items-center'>
                <div className='flex-shrink-0 p-2 md:p-3 bg-gradient-to-r from-yellow-500 to-yellow-600 text-white rounded-xl shadow-lg opacity-60'>
                  <TrophyIcon className='h-6 w-6 md:h-8 md:w-8' />
                </div>
                <div className='ml-3 md:ml-4'>
                  <p className='text-xs md:text-sm font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide'>
                    Current Rank
                  </p>
                  <p className='text-xl md:text-3xl font-bold text-gray-400 dark:text-gray-500'>-</p>
                </div>
              </div>
              <span className='text-[10px] font-bold text-amber-700 dark:text-amber-300 px-2.5 py-1 rounded-full shadow-sm'>
                Coming Soon
              </span>
            </div>
          </div>

          {/* Streak Card */}
          <div className='bg-white/95 dark:bg-gray-800/95 rounded-2xl shadow-xl p-4 md:p-6 border border-gray-200/50 dark:border-gray-700/50 hover:shadow-2xl transition-shadow duration-300 relative'>
            <div className='flex items-center justify-between'>
              <div className='flex items-center'>
                <div className='flex-shrink-0 p-2 md:p-3 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-xl shadow-lg opacity-60'>
                  <FireIcon className='h-6 w-6 md:h-8 md:w-8' />
                </div>
                <div className='ml-3 md:ml-4'>
                  <p className='text-xs md:text-sm font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide'>
                    Win Streak
                  </p>
                  <p className='text-xl md:text-3xl font-bold text-gray-400 dark:text-gray-500'>-</p>
                  <p className='text-xs md:text-sm text-gray-400 dark:text-gray-500 mt-1'>
                    Best: <span className='font-semibold'>-</span>
                  </p>
                </div>
              </div>
              <span className='text-[10px] font-bold text-amber-700 dark:text-amber-300 px-2.5 py-1 rounded-full shadow-sm'>
                Coming Soon
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Rewards Table */}
      <div className='bg-white/95 dark:bg-gray-800/95 rounded-2xl shadow-xl p-4 md:p-8 border border-gray-200/50 dark:border-gray-700/50'>
        <div className='mb-4 md:mb-6'>
          <h3 className='text-sm md:text-base font-bold text-gray-900 dark:text-white'>Rewards History</h3>
          <p className='text-sm md:text-base text-gray-600 dark:text-gray-400 mt-1'>
            Track your earnings and rewards from games
          </p>
        </div>
        <div className='max-w-[calc(100vw-84px)] overflow-x-auto'>
          <RewardsTable data={rewardsData} />
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
